// @flow
import React, { useState, type ElementRef, type Node } from 'react';
import { useOnVisible } from '@ta/common.is-visible';

/** Component props for a <LazyImage> */
type Props = {|
  /** Alt text for accessibility */
  alt: string,

  /** The css class name */
  className?: string,

  /**
   * If specified, displays a customized component instead of the <img> before image loads.
   * Has to contain an element that has the ref. otherwise the image wouldn't load.
   */
  loadingElement?: (ElementRef<*>) => Node,

  /** A 'load' event handler for when the image is scrolled into view and loaded */
  onLoad?: (SyntheticEvent<any>) => mixed,

  /** The expected width of the image once rendered */
  sizes?: string,

  /** The image URL */
  src: string,

  /**
   * A set of source image URLs and their corresponding widths in the format:
   * "small.jpg 100w, medium.jpg 200w, large.jpg 300w"
   */
  srcSet?: string,

  /**
   * The number of pixels prior to the element coming on screen at which point we want to actually load the
   * image. This applies to both vertical as well as horizontal scrolling. If you are seeing the image popping in
   * as you scroll then you can increase this value to load them more aggressively at the cost of potentially loading
   * images that are never seen.
   */
  threshold?: number,
|};

/**
 * A lazy loaded image. It will render an image tag (or the loadingElement) but withhold any attributes that would
 * cause the actual image to load including 'src' and 'srcset'. It then uses the `useOnVisible` hook to determine if
 * the component came in view  and re-render and include the remaining attributes.
 */
const LazyImage = ({
  className,
  loadingElement,
  onLoad,
  threshold = 200,
  sizes,
  src,
  alt,
  srcSet,
}: Props) => {
  const [hasBeenSeen, setHasBeenSeen] = useState(false);

  const visibilityContainerRef = useOnVisible(() => setHasBeenSeen(true), threshold);

  if (hasBeenSeen === false) {
    if (loadingElement) {
      return loadingElement(visibilityContainerRef);
    }
    return (
      <img
        className={className}
        onLoad={onLoad}
        ref={visibilityContainerRef}
        sizes={sizes}
        alt={alt}
      />
    );
  }
  return (
    <img className={className} onLoad={onLoad} sizes={sizes} src={src} srcSet={srcSet} alt={alt} />
  );
};

export default LazyImage;
